import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '../../../services/api';
import RichTextDisplay from '../../../components/RichTextDisplay';
import AttachmentTab from '../../../components/AttachmentTab';
import { hasActualTextContent, stripHtml } from '../../../utils/sanitize';

type ChangePanelTab = 'change' | 'planning' | 'schedule' | 'approver' | 'attachment';

interface WorkbenchChangePanelProps {
  meetingId: string;
  changeRequestId: string | null | undefined;
}

interface ApprovalRow {
  id: string;
  stateKey: string;
  stateLabel: string;
  approver: string;
  assignmentGroup: string | null;
  comments: string;
  createdAt: string | null;
}

function formatPerson(user: {
  firstName?: string;
  lastName?: string;
  email?: string;
} | null | undefined): string {
  if (!user) return '—';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || '—';
}

function formatScheduleDate(value: string | null | undefined): string {
  if (!value) return 'Not Available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not Available';
  return format(date, 'yyyy-MM-dd HH:mm:ss');
}

function formatApprovalCreated(value: string | Date | null | undefined): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'yyyy-MM-dd HH:mm:ss');
}

function approvalStateLabel(state: string | null | undefined): string {
  switch ((state || '').toLowerCase()) {
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'pending':
      return 'Pending';
    case 'cancelled':
    case 'invalidated':
      return 'No Longer Required';
    default:
      return state ? state.replace(/_/g, ' ') : 'Unknown';
  }
}

function approvalStateDotClass(stateKey: string): string {
  switch (stateKey.toLowerCase()) {
    case 'approved':
      return 'bg-green-500';
    case 'rejected':
      return 'bg-red-500';
    case 'pending':
      return 'bg-amber-400';
    case 'cancelled':
    case 'invalidated':
      return 'bg-gray-400';
    default:
      return 'bg-gray-400';
  }
}

function assignmentGroupForStage(
  stage: string | null | undefined,
  change: any
): string | null {
  switch (stage) {
    case 'peer_review':
      return (
        change?.Group_Case_peerReviewGroupIdToGroup?.name ||
        change?.peerReviewGroup?.name ||
        'Peer Review Group'
      );
    case 'cab':
      return change?.Group_Case_approvalGroupIdToGroup?.name || 'CAB Approval';
    case 'customer':
      return 'Customer';
    default:
      return change?.Group_Case_approvalGroupIdToGroup?.name || null;
  }
}

function Field({
  label,
  value,
  fullWidth,
}: {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <div className={fullWidth ? 'col-span-2' : ''}>
      <div className="text-xs text-gray-500 mb-0.5">{label}</div>
      <div className="text-sm text-gray-900 min-h-[1.5rem]">{value || '—'}</div>
    </div>
  );
}

function PlanningSection({ title, content }: { title: string; content?: string | null }) {
  const hasContent = hasActualTextContent(content || '');
  return (
    <div className="mb-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-2">{title}</h3>
      <div className="border border-gray-200 rounded-md bg-gray-50 px-3 py-2 max-h-64 overflow-auto">
        {hasContent ? (
          <RichTextDisplay content={content || ''} className="text-sm text-gray-800" />
        ) : (
          <p className="text-sm text-gray-500">No data available</p>
        )}
      </div>
    </div>
  );
}

function ScheduleField({ label, value, required }: { label: string; value: string; required?: boolean }) {
  return (
    <div className="mb-4">
      <label className="block text-sm text-gray-700 mb-1">
        {required ? <span className="text-red-500 mr-0.5">*</span> : null}
        {label}
      </label>
      <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-900">
        {value}
      </div>
    </div>
  );
}

function buildApprovalRows(change: any, workflow: any): ApprovalRow[] {
  const rows: ApprovalRow[] = [];
  const seen = new Set<string>();

  const approvals = Array.isArray(change?.approvals) ? change.approvals : [];
  for (const approval of approvals) {
    const id = approval.id || `approval-${approval.approverId}-${approval.approvalStage}`;
    seen.add(`${approval.approverId || ''}:${approval.approvalStage || ''}`);
    const stateKey = String(approval.state || 'pending').toLowerCase();
    rows.push({
      id,
      stateKey,
      stateLabel: approvalStateLabel(stateKey),
      approver: formatPerson(approval.User),
      assignmentGroup: assignmentGroupForStage(approval.approvalStage, change),
      comments: approval.comments || '',
      createdAt: approval.approvedAt || approval.createdAt || null,
    });
  }

  const peerMembers = workflow?.stages?.peer_review?.members || workflow?.peerReviewMembers || [];
  for (const member of peerMembers) {
    const key = `${member.userId || ''}:peer_review`;
    if (seen.has(key)) continue;
    // Surface invalidated (and other peer statuses not already in Approval rows)
    if (member.status !== 'invalidated' && member.status !== 'pending') continue;
    if (member.status === 'pending' && approvals.some((a: any) => a.approverId === member.userId)) {
      continue;
    }
    seen.add(key);
    const stateKey = String(member.status || 'pending').toLowerCase();
    rows.push({
      id: `peer-${member.userId}-${stateKey}`,
      stateKey,
      stateLabel: approvalStateLabel(stateKey),
      approver: formatPerson(member),
      assignmentGroup: assignmentGroupForStage('peer_review', change),
      comments: member.comments || member.invalidatedReason || '',
      createdAt: member.respondedAt || member.invalidatedAt || null,
    });
  }

  rows.sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return tb - ta;
  });

  return rows;
}

const CabWorkbenchChangePanel: React.FC<WorkbenchChangePanelProps> = ({
  meetingId,
  changeRequestId,
}) => {
  const [activeTab, setActiveTab] = useState<ChangePanelTab>('change');
  const [selectedApprovalIds, setSelectedApprovalIds] = useState<Set<string>>(new Set());

  const { data: change, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['workbench-change', meetingId, changeRequestId],
    queryFn: async () => {
      const response = await api.get(
        `/cab-workbench/meetings/${meetingId}/changes/${changeRequestId}`
      );
      return response.data;
    },
    enabled: Boolean(meetingId && changeRequestId),
    staleTime: 15_000,
    retry: (failureCount, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403 || status === 404) return false;
      return failureCount < 2;
    },
  });

  const {
    data: workflowStatus,
    isLoading: isApprovalsLoading,
    isError: isApprovalsError,
  } = useQuery({
    queryKey: ['workbench-change-approvals', changeRequestId],
    queryFn: async () => {
      const response = await api.get(`/changes/${changeRequestId}/approval-workflow/status`);
      return response.data;
    },
    enabled: Boolean(changeRequestId) && activeTab === 'approver',
    staleTime: 15_000,
  });

  useEffect(() => {
    setSelectedApprovalIds(new Set());
    setActiveTab('change');
  }, [changeRequestId]);

  useEffect(() => {
    const hasLocalApprovals = Array.isArray(change?.approvals) && change.approvals.length > 0;
    if (activeTab === 'approver' && isApprovalsError && !hasLocalApprovals) {
      toast.error('Unable to load approval records.');
    }
  }, [activeTab, isApprovalsError, change?.approvals]);

  const approvalRows = useMemo(
    () => (change ? buildApprovalRows(change, workflowStatus) : []),
    [change, workflowStatus]
  );

  if (!changeRequestId) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-gray-500 p-8">
        No pending agenda items remaining.
      </div>
    );
  }

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading change request...</div>;
  }

  if (isError || !change) {
    const apiMessage = (error as { response?: { data?: { message?: string } } })?.response
      ?.data?.message;
    return (
      <div className="p-6 text-sm text-red-600 space-y-3">
        <p>{apiMessage || 'Unable to load Change Request details.'}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="px-3 py-1.5 text-sm text-white bg-teal-700 rounded-md hover:bg-teal-800"
        >
          Retry
        </button>
      </div>
    );
  }

  const assignedTo = change.User_Case_assignedToIdToUser;
  const assignmentGroup = change.Group_Case_assignmentGroupIdToGroup;
  const requestedBy = change.User_Case_callerIdToUser || change.User_Case_reportedByIdToUser;
  const title = `${change.number || ''} - ${change.shortDescription || ''}`.trim();

  const allApprovalIds = approvalRows.map((row) => row.id);
  const allSelected =
    approvalRows.length > 0 && approvalRows.every((row) => selectedApprovalIds.has(row.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedApprovalIds(new Set());
    } else {
      setSelectedApprovalIds(new Set(allApprovalIds));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedApprovalIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const tabClass = (tab: ChangePanelTab) =>
    `py-2 text-sm border-b-2 transition-colors ${
      activeTab === tab
        ? 'font-medium text-teal-700 border-teal-600'
        : 'text-gray-500 border-transparent hover:text-gray-800'
    }`;

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900 leading-snug">{title}</h2>
      </div>

      <div className="border-b border-gray-200 px-4 flex items-center gap-4 overflow-x-auto">
        <button type="button" className={tabClass('change')} onClick={() => setActiveTab('change')}>
          Change
        </button>
        <button
          type="button"
          className={tabClass('planning')}
          onClick={() => setActiveTab('planning')}
        >
          Planning
        </button>
        <button
          type="button"
          className={tabClass('schedule')}
          onClick={() => setActiveTab('schedule')}
        >
          Schedule
        </button>
        <button
          type="button"
          className={tabClass('approver')}
          onClick={() => setActiveTab('approver')}
        >
          Approver
        </button>
        <button
          type="button"
          className={tabClass('attachment')}
          onClick={() => setActiveTab('attachment')}
        >
          Attachment
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {activeTab === 'change' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <Field label="Number" value={change.number} />
            <Field label="Type" value={change.changeType || change.ticketType} />
            <Field label="Requested by" value={formatPerson(requestedBy)} />
            <Field label="State" value={change.changeState || change.state} />
            <Field label="Category" value={change.category} />
            <Field
              label="On hold"
              value={
                <input
                  type="checkbox"
                  checked={change.changeState === 'on_hold'}
                  readOnly
                  className="h-4 w-4"
                  aria-label="On hold"
                />
              }
            />
            <Field label="Service" value={change.Resource?.name || change.service || '—'} />
            <Field label="Conflict status" value={change.conflictStatus || 'Not Run'} />
            <Field
              label="Configuration item"
              value={change.configurationItem || change.ci || '—'}
            />
            <Field label="Conflict last run" value={change.conflictLastRun || '—'} />
            <Field
              label="Priority"
              value={change.priority != null ? String(change.priority) : '—'}
            />
            <Field label="Assignment group" value={assignmentGroup?.name || '—'} />
            <Field label="Risk" value={change.risk || '—'} />
            <Field label="Assigned to" value={formatPerson(assignedTo)} />
            <Field label="Impact" value={change.impact || '—'} />
            <Field label="Short description" value={change.shortDescription} fullWidth />
            <Field
              label="Description"
              value={
                <div className="whitespace-pre-wrap text-sm text-gray-800 border border-gray-200 rounded p-2 bg-gray-50 max-h-48 overflow-auto">
                  {stripHtml(change.description || '') || '—'}
                </div>
              }
              fullWidth
            />
            <Field
              label="On hold reason"
              value={
                <div className="whitespace-pre-wrap text-sm text-gray-800 border border-gray-200 rounded p-2 bg-gray-50 min-h-[3rem]">
                  {change.onHoldReason || ''}
                </div>
              }
              fullWidth
            />
          </div>
        )}

        {activeTab === 'planning' && (
          <div className="max-w-4xl">
            <PlanningSection title="Justification" content={change.justification} />
            <PlanningSection
              title="Implementation plan"
              content={change.implementationPlan}
            />
            <PlanningSection
              title="Risk and impact analysis"
              content={change.riskImpactAnalysis}
            />
            <PlanningSection title="Backout plan" content={change.backoutPlan} />
            <PlanningSection title="Test plan" content={change.testPlan} />
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="max-w-xl">
            <ScheduleField
              label="Planned start date"
              value={formatScheduleDate(change.plannedStart || change.plannedStartDate)}
            />
            <ScheduleField
              label="Planned end date"
              value={formatScheduleDate(change.plannedEnd || change.plannedEndDate)}
            />
            <ScheduleField
              label="Actual start date"
              value={formatScheduleDate(change.actualStart)}
              required
            />
            <ScheduleField
              label="Actual end date"
              value={formatScheduleDate(change.actualEnd)}
              required
            />
          </div>
        )}

        {activeTab === 'approver' && (
          <div>
            <div className="bg-gray-100 border border-gray-200 px-3 py-2 text-sm text-gray-800 mb-0">
              Approval for = {change.number || changeRequestId}
            </div>

            {isApprovalsLoading && !workflowStatus && approvalRows.length === 0 ? (
              <div className="p-6 text-sm text-gray-500 border border-t-0 border-gray-200">
                Loading approval records...
              </div>
            ) : isApprovalsError && approvalRows.length === 0 ? (
              <div className="p-6 text-sm text-red-600 border border-t-0 border-gray-200">
                Unable to load approval records.
              </div>
            ) : approvalRows.length === 0 ? (
              <div className="p-6 text-sm text-gray-500 border border-t-0 border-gray-200">
                No approval records found.
              </div>
            ) : (
              <div className="border border-t-0 border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-white">
                    <tr>
                      <th scope="col" className="w-10 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          aria-label="Select all approvals"
                        />
                      </th>
                      <th scope="col" className="w-8 px-1 py-2 text-gray-400">
                        <MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium text-gray-600"
                      >
                        State
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium text-gray-600"
                      >
                        Approver
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium text-gray-600"
                      >
                        Assignment group
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium text-gray-600"
                      >
                        Comments
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium text-gray-600"
                      >
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {approvalRows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={selectedApprovalIds.has(row.id)}
                            onChange={() => toggleSelectOne(row.id)}
                            className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                            aria-label={`Select approval ${row.approver}`}
                          />
                        </td>
                        <td className="px-1 py-2.5" />
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className="inline-flex items-center gap-2 text-sm text-blue-600">
                            <span
                              className={`inline-block h-2.5 w-2.5 rounded-full ${approvalStateDotClass(row.stateKey)}`}
                              aria-hidden="true"
                            />
                            {row.stateLabel}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm text-blue-600">
                          {row.approver}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                          {row.assignmentGroup ? (
                            <span className="text-blue-600">{row.assignmentGroup}</span>
                          ) : (
                            <span className="text-gray-400">(empty)</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-sm text-gray-700 max-w-xs truncate">
                          {row.comments}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-800">
                          {formatApprovalCreated(row.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'attachment' && (
          <AttachmentTab
            entityType="case"
            entityId={changeRequestId}
            canUpload={false}
            canDelete={false}
            hideUploadArea
            emptyMessage="No attachments found."
          />
        )}
      </div>
    </div>
  );
};

export default CabWorkbenchChangePanel;
